import { ITrigger } from 'types'

export type ITriggerNoId = Omit<ITrigger, 'publicId'>

// Hub and document events are listed under their product names (`hub.*`, `document.*`). A trigger saved
// under a deprecated legacy name (`project.*`, `process.*`) keeps it, since that's the `event` name its
// deliveries use, but is shown and matched under the product name.
const PRODUCT_RESOURCE_NAMES: Record<string, string> = { project: 'hub', process: 'document' }

export const toEventName = (trigger: ITriggerNoId): string =>
  `${PRODUCT_RESOURCE_NAMES[trigger.resource] ?? trigger.resource}.${trigger.verb}`

// How a saved trigger is listed: the product name, plus the name actually sent in `event` if it's a legacy one
export const toEventLabel = (trigger: ITriggerNoId): string => {
  const eventName = toEventName(trigger)
  const sentAs = `${trigger.resource}.${trigger.verb}`
  return eventName === sentAs ? eventName : `${eventName} (sent as ${sentAs})`
}

// A webhook's events as listed: a legacy-name trigger is left out when the webhook also has the product-name
// trigger for the same event, since only the product-name delivery is sent then
export const eventLabelsForWebhook = (triggers: ITriggerNoId[]): string[] => {
  const isLegacyName = (trigger: ITriggerNoId) => toEventName(trigger) !== `${trigger.resource}.${trigger.verb}`
  const productNamed = new Set(triggers.filter((trigger) => !isLegacyName(trigger)).map(toEventName))
  const listed = triggers.filter((trigger) => !(isLegacyName(trigger) && productNamed.has(toEventName(trigger))))
  return [...new Set(listed.map(toEventLabel))]
}

const toTrigger = (eventName: string): ITriggerNoId => {
  const [resource, verb] = eventName.split('.')
  return { resource, verb }
}

// Build the trigger list for the selected events, keeping every already-saved trigger as it is
// (so a legacy `process.updated` subscription isn't silently renamed to `document.updated`, and a
// webhook subscribed under both names keeps both)
export const triggersForEvents = (triggers: ITriggerNoId[], eventNames: string[]): ITriggerNoId[] => {
  const kept = triggers.filter((trigger) => eventNames.includes(toEventName(trigger)))
  const keptNames = kept.map(toEventName)
  const added = eventNames.filter((eventName) => !keptNames.includes(eventName)).map(toTrigger)
  return [...kept, ...added]
}

export const isTableTriggerSelected = (triggers: ITriggerNoId[]) => {
  return triggers.find((t) => t.resource === 'table') ? true : false
}

export const isProcessTriggerSelected = (triggers: ITriggerNoId[]) => {
  return triggers.find((t) => t.resource === 'process' || t.resource === 'document') ? true : false
}

/** "1 event", "3 events". */
export const eventCount = (count: number): string => `${count} ${count === 1 ? 'event' : 'events'}`
